import { useState } from 'react';

import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { commonTranslationKeys, createTranslatedText } from '@dreryk/react-common';
import { Dropdown, DropdownPlacement, LinkIconVariant, LinkVariant, StyledFlex } from '@dreryk/react-components';

import { VisitLocationData, VisitLocationId } from './VisitLocation.types';
import { useFirstVisitLocationSelector, useVisitLocationUpdateCommand } from './VisitLocationDropdown.hooks';
import { StyledVisitLocationDropdownItem } from './VisitLocationDropdownItem.styled';
import { VisitLocationDropdownItemContent } from './VisitLocationDropdownItemContent';
import { VisitLocationIcon } from './VisitLocationIcon';
import { VisitLocationTooltip } from './VisitLocationTooltip';

type VisitLocationDropdownProps = {
   readonly visitLocations: VisitLocationData[];
   readonly visitId: Visit['id'];
   readonly disabled: boolean;
   readonly initialVisitLocationId: VisitLocationId | undefined;
};

export const VisitLocationDropdown = (props: VisitLocationDropdownProps): JSX.Element => {
   const [visitLocation, setVisitLocation] = useState<VisitLocationData | null>(() => {
      if (!props.initialVisitLocationId) {
         return null;
      }

      const foundVisitLocation = props.visitLocations.find((location) => {
         if (!props.initialVisitLocationId) {
            return false;
         }

         const sameId = props.initialVisitLocationId.id === location.id;
         const sameType = props.initialVisitLocationId.type === location.type;
         return sameId && sameType;
      });

      return foundVisitLocation ?? null;
   });

   const { executeAsync: updateVisitLocation } = useVisitLocationUpdateCommand({
      visitId: props.visitId,
      onSuccess: (visitLocation) => {
         setVisitLocation(visitLocation);
      },
   });

   useFirstVisitLocationSelector({
      chosenVisitLocationId: visitLocation?.id,
      visitLocations: props.visitLocations,
      onVisitLocationChange: async (visitLocation) =>
         updateVisitLocation({
            visitLocation: visitLocation,
         }),
   });

   return (
      <VisitLocationTooltip name={visitLocation?.name} address={visitLocation?.address}>
         <StyledFlex gap='4px' alignItems='center'>
            <VisitLocationIcon />
            <Dropdown
               testId={'visitLocation'}
               triggerLabel={visitLocation ? createTranslatedText(visitLocation.name) : commonTranslationKeys.choose}
               variant={LinkVariant.STANDARD}
               triggerIconSuffixVariant={LinkIconVariant.PRIMARY}
               placement={DropdownPlacement.BOTTOM_START}
               disabled={props.disabled}
               maxWidth={298}
            >
               {props.visitLocations.map((visitLocation) => (
                  <StyledVisitLocationDropdownItem
                     key={`${visitLocation.type}-${visitLocation.id}`}
                     testId={`${visitLocation.type}-${visitLocation.id}`}
                     onClick={async (hide) =>
                        updateVisitLocation({
                           visitLocation: visitLocation,
                        }).finally(hide)
                     }
                  >
                     <VisitLocationDropdownItemContent label={visitLocation.name} address={visitLocation.address} />
                  </StyledVisitLocationDropdownItem>
               ))}
            </Dropdown>
         </StyledFlex>
      </VisitLocationTooltip>
   );
};
